import { twMerge } from "tailwind-merge";
import { PageContainer } from "../../../components/pageContainer/PageContainer";
import { SupplierPalletShipmentGetResponseType } from "@sellar-io/api-v2/types";
import { useNavigate } from "react-router";
import { useDashboardContext } from "../../../components/dashboard/Dashboard.shared";
import { useState } from "react";

import { NavigationBar } from "../../../components/navigation/NavigationBar";
import {
  DISTRIBUTION_PALLET_SHIPMENT_DETAILS,
  DISTRIBUTION_PALLET_SHIPMENTS,
} from "../../../constants/routes";
import { useParamAsNumber } from "../../../hooks/useParamAsNumber";
import { SpinnerLegacy } from "../../../components";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  DataButton,
  H5,
  H6,
  Icon,
  createSellarColumnHelper,
} from "@sellar-io/components";
import { pluralize } from "@sellar-io/common";
import dayjs from "dayjs";
import { TableInfinite } from "../../../components/table/TableInfinite";
import { CollectionOrderDrawer } from "../../collections/CollectionOrderDrawer";
import { useSellarDistributionPalletShipment } from "../hooks/useSellarDistributionPalletShipment";
import { shipmentStatus } from "../DistributionPalletShipmentListItem.shared";
import { useSellarDistributionPalletDispatch } from "../hooks/useSellarDistributionPalletDispatch";
import { DistributionFinaliseDialog } from "./components/DistributionFinaliseDialog";
import { Alert } from "../../../components/alert/Alert";
import { AdminOnly } from "../../../components/authFilter/AdminOnly";
import { useSellarDownloadPalletPickingList } from "../hooks/useSellarDownloadPalletPickingList";
import { downloadPDF } from "../../../utils/downloadHelpers";
import {
  InlineTab,
  InlineTabGroup,
  InlineTabList,
  InlineTabPanel,
  InlineTabPanels,
} from "../../../components/inlineTab/InlineTab";

const imageURl =
  "https://sellar.imgix.net/sellar-image-assets/sellar-logo-gradient.svg";

export const DistributionPalletShipmentDetails = () => {
  const palletShipmentId = useParamAsNumber(
    DISTRIBUTION_PALLET_SHIPMENT_DETAILS,
    "palletShipmentId",
  );

  const { data: pallet, isLoading } =
    useSellarDistributionPalletShipment(palletShipmentId);

  const dispatchPalletMutation =
    useSellarDistributionPalletDispatch(palletShipmentId);

  const [finaliseDialogActive, setFinaliseDialogActive] = useState(false);

  const handleFinalise = () => {
    setFinaliseDialogActive(true);
  };

  return (
    <>
      <DistributionDetailsContent
        pallet={pallet}
        isLoading={isLoading}
        onDispatch={() => dispatchPalletMutation.mutate()}
        onFinalise={handleFinalise}
      />
      <DistributionFinaliseDialog
        palletShipmentId={palletShipmentId}
        active={finaliseDialogActive}
        onDismiss={() => setFinaliseDialogActive(false)}
      />
    </>
  );
};

export const DistributionDetailsContent = ({
  pallet,
  isLoading,
  onDispatch,
  onFinalise,
}: {
  pallet?: SupplierPalletShipmentGetResponseType;
  isLoading: boolean;
  onDispatch: () => void;
  onFinalise: () => void;
}) => {
  const { isTwoPanel } = useDashboardContext();
  const navigate = useNavigate();

  const [orderDrawerActive, setOrderDrawerActive] = useState(false);
  const [orderId, setOrderId] = useState<number>();
  const [pickingListView, setPickingListView] = useState<"sku" | "order">(
    "sku",
  );

  const downloadPickingListMutation = useSellarDownloadPalletPickingList();

  const handlePrint = () => {
    if (!pallet) return;

    downloadPickingListMutation.mutate(
      { palletId: pallet.id, view: pickingListView },
      {
        onSuccess: (base64Pdf) => {
          downloadPDF(`Picking List - Pallet #${pallet.id}.pdf`, base64Pdf);
        },
      },
    );
  };

  const columnHelper =
    createSellarColumnHelper<
      SupplierPalletShipmentGetResponseType["skus"][number]
    >();

  const skuColumns = [
    columnHelper.accessor("sku", {
      header: "SKU",
      enableSorting: false,
    }),
    columnHelper.accessor("name", {
      header: "Product name",
      enableSorting: false,
      cell: (info) => {
        const productName = info.getValue();
        const name = productName.split(" • ")[0];
        return name;
      },
    }),
    columnHelper.accessor("name", {
      header: "Format",
      enableSorting: false,
      id: "format",
      cell: (info) => {
        const productName = info.getValue();
        const container = productName.split(" • ")[1];
        return container || "-";
      },
    }),
    columnHelper.accessor("quantity", {
      header: "Quantity",
      enableSorting: false,
    }),
    columnHelper.accessor("orderIds", {
      header: "Orders",
      enableSorting: false,
      cell: ({ getValue }) => {
        const orderIds = getValue();
        const nbOrders = orderIds.length;
        return (
          <span className="flex cursor-pointer gap-1">
            {getValue().map((id, index) => (
              <span
                key={id}
                className="underline"
                onClick={() => {
                  setOrderId(id);
                  setOrderDrawerActive(true);
                }}
              >
                #{id}
                {index < nbOrders - 1 ? "," : ""}
              </span>
            ))}
          </span>
        );
      },
    }),
  ];

  const orderColumnHelper =
    createSellarColumnHelper<
      SupplierPalletShipmentGetResponseType["orderItems"][number]
    >();

  const orderColumns = [
    orderColumnHelper.accessor("orderId", {
      header: "Order ID",
      enableSorting: false,
      cell: ({ getValue }) => (
        <button
          type="button"
          className="cursor-pointer underline"
          onClick={() => {
            setOrderId(getValue());
            setOrderDrawerActive(true);
          }}
        >
          #{getValue()}
        </button>
      ),
    }),
    orderColumnHelper.accessor("customerName", {
      header: "Customer",
      enableSorting: false,
      cell: ({ getValue }) => (
        <span className="wrap-anywhere">{getValue()}</span>
      ),
    }),
    orderColumnHelper.accessor("sku", {
      header: "SKU",
      enableSorting: false,
    }),
    orderColumnHelper.accessor("name", {
      header: "Product name",
      enableSorting: false,
      cell: ({ getValue }) => getValue().split(" • ")[0],
    }),
    orderColumnHelper.accessor("name", {
      id: "format",
      header: "Format",
      enableSorting: false,
      cell: ({ getValue }) => getValue().split(" • ")[1] || "-",
    }),
    orderColumnHelper.accessor("quantity", {
      header: "Quantity",
      enableSorting: false,
    }),
  ];

  const collectionTitle = () => {
    switch (pallet?.status) {
      case "open":
      case "readyToShip":
        return "Send on";
      case "dispatched":
        return "Sent on";
      case "received":
        return "Received on";
      default:
        return "Send on";
    }
  };

  const collectionDate =
    pallet?.status === "received"
      ? pallet.deliveredAt
      : pallet?.status === "dispatched"
        ? pallet.dispatchedAt
        : (pallet?.dispatchPlannedAt ?? pallet?.expectedFinaliseDate);

  const palletDispatchDate =
    pallet?.dispatchedAt ??
    pallet?.dispatchPlannedAt ??
    pallet?.expectedFinaliseDate;

  const palletDeliveryDateDate =
    pallet?.deliveredAt ??
    pallet?.deliveryPlannedAt ??
    pallet?.expectedArrivalDate;

  const getDateColorClass = (
    status: SupplierPalletShipmentGetResponseType["status"] | undefined,
  ) => {
    switch (status) {
      case "open":
        return "text-orange-500";
      case "readyToShip":
        return "text-green-600";
      case "dispatched":
        return "text-purple-600";
      case "received":
        return "text-blue-600";
      default:
        return "text-zinc-900";
    }
  };

  const formattedShipmentTotalWeight =
    typeof pallet?.shipmentInfo.totalWeight === "number"
      ? `${pallet.shipmentInfo.totalWeight}kg`
      : "N/A";

  const palletFinalised = !!pallet?.finalisedAt;
  const palletDispatched = !!pallet?.dispatchedAt;
  const palletDelivered = !!pallet?.deliveredAt;

  const canShowPalletDispatchAction =
    pallet && !palletDispatched && !palletDelivered && palletFinalised;

  const canShowPalletFinaliseAction =
    pallet && !palletDispatched && !palletDelivered && !palletFinalised;

  return (
    <>
      <NavigationBar
        title="Shipment details"
        leftIcon="CHEVRON_LEFT"
        onClickLeftIcon={
          isTwoPanel ? undefined : () => navigate(DISTRIBUTION_PALLET_SHIPMENTS)
        }
      />
      <PageContainer className={"flex flex-col gap-2"}>
        {isLoading || !pallet ? (
          <SpinnerLegacy />
        ) : (
          <>
            <Card className="bg-white">
              <CardHeader className="flex items-center justify-between gap-4 bg-white p-4">
                <div className="flex flex-col">
                  <H5>
                    Sellar Distribution • {pallet.distributionWarehouse.name}
                  </H5>
                  <H6 className="text-zinc-500">
                    {pallet.nbOfOrders} {pluralize("order", pallet.nbOfOrders)}{" "}
                    • {pallet.nbOfSkus} {pluralize("SKU", pallet.nbOfSkus)}
                  </H6>
                </div>
                {shipmentStatus(pallet.status)}
              </CardHeader>

              <CardContent className="p-4">
                <Alert
                  type="information"
                  title="Important"
                  message={
                    <div className="flex flex-col gap-2">
                      <div>
                        Please send your shipment via next day AM delivery
                        service.
                      </div>

                      <DataButton
                        border={true}
                        onClick={(e) => {
                          e.preventDefault();
                          window.open(
                            `https://intercom.help/sellario/en/articles/13726326-getting-your-orders-to-us#h_431e618b3d`,
                          );
                        }}
                        loading={downloadPickingListMutation.isPending}
                      >
                        Find out more
                      </DataButton>
                    </div>
                  }
                  className="mb-4 w-full"
                />

                <div className="flex flex-col items-center gap-8 rounded-lg bg-zinc-100 p-4">
                  <div className="flex flex-col items-center gap-2">
                    <img
                      alt={"pallet-image"}
                      src={imageURl}
                      className={"h-12 w-12"}
                    />
                    <H5>Sellar Distribution</H5>
                  </div>

                  <div className="flex items-center justify-center gap-4 sm:gap-16">
                    <div className="flex flex-col items-center gap-1">
                      <H5>Pallet destination:</H5>
                      <span className="text-2xl font-bold">
                        {pallet.distributionWarehouse.name}
                      </span>
                    </div>
                    <div className="flex flex-col items-center gap-1">
                      <H5>{collectionTitle()}:</H5>
                      <span
                        className={`text-2xl font-bold ${getDateColorClass(
                          pallet.status,
                        )}`}
                      >
                        {collectionDate
                          ? dayjs(collectionDate).format("DD/MM/YYYY")
                          : "TBC"}
                      </span>
                    </div>
                  </div>
                </div>

                {canShowPalletDispatchAction && (
                  <div className="pt-4">
                    <Button
                      color="primary"
                      size="large"
                      className="w-full"
                      onClick={onDispatch}
                    >
                      Dispatch
                    </Button>

                    <div className="py-4 text-center font-semibold text-zinc-600">
                      Click ‘Dispatch’ when the goods listed below have left
                      your premises.
                    </div>
                  </div>
                )}
                <AdminOnly>
                  {canShowPalletFinaliseAction && (
                    <div className="pt-4">
                      <Button
                        color="primary"
                        size="large"
                        className="w-full"
                        onClick={onFinalise}
                      >
                        Finalise
                      </Button>

                      <div className="py-4 text-center font-semibold text-zinc-600">
                        Click 'Finalise' when you are ready
                      </div>
                    </div>
                  )}
                </AdminOnly>
              </CardContent>
            </Card>

            <Card className="bg-white">
              <CardHeader
                className={twMerge(
                  "bg-white",
                  "flex items-center justify-between p-4",
                )}
              >
                <H5>
                  Pallet Shipment info: {pallet.distributionWarehouse.name}
                </H5>
              </CardHeader>
              <CardContent className={"p-4"}>
                <div className="flex flex-col gap-6">
                  <div className="grid md:grid-cols-2">
                    <div className="col-span-1 flex flex-col gap-4">
                      <div>
                        <H5>Destination address</H5>
                        <H6>
                          {pallet.distributionWarehouse.name}
                          <br />
                          {pallet.distributionWarehouse.address.street}
                          <br />
                          {pallet.distributionWarehouse.address.city}
                          <br />
                          {pallet.distributionWarehouse.address.postalCode}
                        </H6>
                      </div>
                    </div>
                    <div className="col-span-1 flex flex-col gap-4">
                      <div>
                        <H5>Estimated total weight</H5>
                        <H6>{formattedShipmentTotalWeight}</H6>
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2">
                    <div className="col-span-1 flex flex-col gap-4">
                      <div className="">
                        <H5>Pallet dispatch date</H5>
                        <H6>
                          {palletDispatchDate
                            ? dayjs(palletDispatchDate).format("DD/MM/YYYY")
                            : "TBC"}
                        </H6>
                      </div>
                    </div>
                    <div className="col-span-1 flex flex-col gap-4">
                      <div className="">
                        <H5>Pallet delivery date</H5>
                        <H6>
                          {palletDeliveryDateDate
                            ? dayjs(palletDeliveryDateDate).format("DD/MM/YYYY")
                            : "TBC"}
                        </H6>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white">
              <CardHeader className="flex items-center justify-between bg-white p-4">
                <H5>Picking list</H5>
                <DataButton
                  border={true}
                  onClick={handlePrint}
                  loading={downloadPickingListMutation.isPending}
                >
                  Print PDF <Icon icon="PRINTER" size={0.7} />
                </DataButton>
              </CardHeader>
              <CardContent className="flex flex-col gap-4 p-4">
                {pallet.status === "readyToShip" && (
                  <Alert
                    type="success"
                    title="These orders are due to be shipped today"
                    message="Get the following order items ready for SD Pallet Shipment by the allotted collection time. Don’t forget to hit ‘Dispatch’ when they’ve left your premises."
                  />
                )}
                {pallet.status === "open" && (
                  <Alert
                    type="information"
                    title="Picking list not final"
                    message="This Sellar Distribution shipment has not yet been finalised. We will send you an email reminder before finalisation."
                  />
                )}
                <InlineTabGroup
                  onChange={(index) =>
                    setPickingListView(index === 0 ? "sku" : "order")
                  }
                >
                  <InlineTabList>
                    <InlineTab>View by SKU</InlineTab>
                    <InlineTab>View by Order</InlineTab>
                  </InlineTabList>
                  <InlineTabPanels className="mt-4">
                    <InlineTabPanel>
                      <TableInfinite
                        columns={skuColumns}
                        data={pallet.skus}
                        infiniteQuery={{
                          data: pallet.skus,
                          isFetching: false,
                          totalCount: pallet.skus.length,
                        }}
                        noResultsText="No products found."
                        gridClasses={`grid-cols-[minmax(70px,_1fr)_minmax(170px,_1fr)_minmax(170px,_1fr)_minmax(80px,_1fr)_minmax(250px,_1fr)]`}
                        roundedTop={true}
                      />
                    </InlineTabPanel>
                    <InlineTabPanel>
                      <TableInfinite
                        columns={orderColumns}
                        data={pallet.orderItems}
                        infiniteQuery={{
                          data: pallet.orderItems,
                          isFetching: false,
                          totalCount: pallet.orderItems.length,
                        }}
                        noResultsText="No products found."
                        gridClasses={`grid-cols-[minmax(100px,_1fr)_minmax(160px,_1fr)_minmax(100px,_1fr)_minmax(170px,_1fr)_minmax(130px,_1fr)_minmax(80px,_1fr)]`}
                        roundedTop={true}
                      />
                    </InlineTabPanel>
                  </InlineTabPanels>
                </InlineTabGroup>
              </CardContent>
            </Card>
          </>
        )}
      </PageContainer>
      <CollectionOrderDrawer
        active={orderDrawerActive}
        setActive={setOrderDrawerActive}
        orderId={orderId}
      />
    </>
  );
};
